import React, { useState, useEffect, useRef, useContext } from "react";
import { getMyImages, uploadMyImage, deleteMyImage, renameMyImage } from "../api";
import { ToastContext } from "../App";

export default function MyImages() {
  const showToast = useContext(ToastContext);
  const [images, setImages]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // blob name pending delete

  // Name-prompt state (shown after file is picked, before upload)
  const [pendingFile, setPendingFile]   = useState(null);
  const [pendingName, setPendingName]   = useState("");

  // Rename state — modal instead of inline
  const [renamingImg, setRenamingImg]   = useState(null); // { name, label }
  const [renameVal, setRenameVal]       = useState("");

  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    getMyImages()
      .then((data) => setImages(data.images || []))
      .catch(() => showToast("Failed to load saved images", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line

  // Step 1: user picks a file → show name prompt instead of uploading immediately
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    // Default name: strip extension from filename
    const defaultName = file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ");
    setPendingName(defaultName);
    setPendingFile(file);
  };

  // Step 2: user confirms name → upload
  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    try {
      setUploading(true);
      setPendingFile(null);
      const result = await uploadMyImage(pendingFile, pendingName.trim());
      setImages((prev) => [result, ...prev]);
      showToast("Image saved!", "success");
    } catch (err) {
      showToast(err.message || "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (name) => {
    try {
      await deleteMyImage(name);
      setImages((prev) => prev.filter((img) => img.name !== name));
      setConfirmDelete(null);
      showToast("Image removed", "default");
    } catch (err) {
      showToast(err.message || "Delete failed", "error");
    }
  };

  const handleRename = async () => {
    if (!renamingImg) return;
    try {
      await renameMyImage(renamingImg.name, renameVal.trim());
      setImages((prev) =>
        prev.map((img) => img.name === renamingImg.name ? { ...img, label: renameVal.trim() } : img)
      );
      setRenamingImg(null);
      showToast("Image renamed", "success");
    } catch (err) {
      showToast(err.message || "Rename failed", "error");
    }
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <div>
      <div className="summary-bar">
        <div>
          <div className="summary-label">My Saved Images</div>
          <div style={{ fontSize: 12, color: "var(--gray-4)", marginTop: 2 }}>
            {images.length} image{images.length !== 1 ? "s" : ""} saved
          </div>
        </div>
        <button
          className="btn btn-primary"
          style={{ padding: "8px 16px", fontSize: 14 }}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Saving…" : "+ Add Image"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      {/* Name prompt modal — shown after file is selected */}
      {pendingFile && (
        <div className="modal-overlay" onClick={() => setPendingFile(null)}>
          <div className="modal" style={{ maxWidth: 340 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title" style={{ marginBottom: 14 }}>Name this image</div>
            <input
              className="form-control"
              type="text"
              placeholder="e.g. Home Depot receipt, Mileage map"
              value={pendingName}
              onChange={(e) => setPendingName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleConfirmUpload()}
              autoFocus
              style={{ marginBottom: 14 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setPendingFile(null)} style={{ flex: 1 }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmUpload} style={{ flex: 2 }}>
                Save Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename modal */}
      {renamingImg && (
        <div className="modal-overlay" onClick={() => setRenamingImg(null)}>
          <div className="modal" style={{ maxWidth: 340 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title" style={{ marginBottom: 14 }}>Rename image</div>
            <input
              className="form-control"
              type="text"
              placeholder="e.g. Home Depot receipt, Mileage map"
              value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRename();
                if (e.key === "Escape") setRenamingImg(null);
              }}
              autoFocus
              style={{ marginBottom: 14 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setRenamingImg(null)} style={{ flex: 1 }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleRename} style={{ flex: 2 }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {images.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🗺️</div>
          <div className="empty-state-title">No saved images yet</div>
          <div className="empty-state-sub">
            Save Google Maps screenshots or any image you reuse often so you can quickly attach it as a receipt without digging through your camera roll.
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 16 }}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? "Saving…" : "+ Add Your First Image"}
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: "12px 0" }}>
          {images.map((img) => (
            <div
              key={img.name}
              style={{
                borderRadius: 12,
                overflow: "hidden",
                background: "var(--gray-1)",
                border: "1.5px solid var(--gray-2)",
                position: "relative",
              }}
            >
              <img
                src={img.url}
                alt={img.label || "Saved image"}
                style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", display: "block" }}
              />

              {/* Delete confirm overlay */}
              {confirmDelete === img.name ? (
                <div style={{
                  position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)",
                  display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", gap: 10, padding: 12,
                }}>
                  <div style={{ color: "white", fontSize: 13, fontWeight: 600, textAlign: "center" }}>
                    Remove this image?
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn"
                      style={{ fontSize: 13, padding: "6px 14px", background: "white", color: "#333", border: "none", borderRadius: 8, fontWeight: 600 }}
                      onClick={() => setConfirmDelete(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn"
                      style={{ fontSize: 13, padding: "6px 14px", background: "var(--red)", color: "white", border: "none", borderRadius: 8 }}
                      onClick={() => handleDelete(img.name)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(img.name)}
                  style={{
                    position: "absolute", top: 6, right: 6,
                    background: "rgba(0,0,0,0.55)", border: "none",
                    borderRadius: "50%", width: 28, height: 28,
                    color: "white", fontSize: 14, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                  title="Remove image"
                >
                  ×
                </button>
              )}

                      {/* Label row below image — tap opens rename modal */}
              <div
                style={{
                  padding: "8px 10px",
                  fontSize: 14,
                  color: img.label ? "var(--gray-5, #222)" : "var(--gray-4)",
                  fontStyle: img.label ? "normal" : "italic",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 4,
                  cursor: "pointer",
                }}
                onClick={() => { setRenamingImg(img); setRenameVal(img.label || ""); }}
                title="Tap to rename"
              >
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {img.label || "Tap to add a name…"}
                </span>
                <span style={{ fontSize: 11, color: "var(--gray-4)", flexShrink: 0 }}>✏️</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
